// ─── Astria GCC v2 — response parser ───────────────────────────────────────
// The GCC v2 backend prompt service (astriaGCCV2Service.js) replies with
// plain markdown ("### Heading" sections), not structured JSON — there is
// no dedicated GCC v2 API contract to rely on (unlike Astria Korea v2's
// astriaKoreaV2Data field). This util turns that markdown into the spec's
// 5-part response structure (soft acknowledgement, emotional understanding,
// personal rhythm insight, calm practical guidance, warm companion closing)
// for card rendering, with a defensive plain-text fallback when the AI
// reply doesn't parse cleanly — mirroring the regex/JSON fallback already
// used by GCCCompatibilityResult.tsx for the older GCC lane.

export type GCCV2SectionKind =
  | "acknowledgement"
  | "understanding"
  | "rhythm"
  | "guidance"
  | "closing"
  | "generic";

export interface GCCV2Section {
  heading: string;
  body: string;
  kind: GCCV2SectionKind;
}

export interface GCCV2ParsedResponse {
  sections: GCCV2Section[];
  raw: string;
}

const KEYWORD_MAP: { kind: GCCV2SectionKind; keywords: string[] }[] = [
  {
    kind: "acknowledgement",
    keywords: ["acknowledg", "welcome", "hear you", "استقبال", "أهلا", "أسمعك"],
  },
  {
    kind: "understanding",
    keywords: ["emotion", "feeling", "understand", "فهم", "شعور", "عاطف"],
  },
  {
    kind: "rhythm",
    keywords: [
      "rhythm",
      "momentum",
      "timing",
      "pattern",
      "إيقاع",
      "زخم",
      "توقيت",
      "نمط",
    ],
  },
  {
    kind: "guidance",
    keywords: [
      "guidance",
      "suggestion",
      "practical",
      "gentle step",
      "إرشاد",
      "اقتراح",
      "خطوة",
    ],
  },
  {
    kind: "closing",
    keywords: ["closing", "companion", "here for you", "خاتمة", "رفيق", "معك"],
  },
];

const POSITIONAL_ORDER: GCCV2SectionKind[] = [
  "acknowledgement",
  "understanding",
  "rhythm",
  "guidance",
  "closing",
];

function classifyHeading(heading: string): GCCV2SectionKind | null {
  const lower = heading.toLowerCase();
  for (const entry of KEYWORD_MAP) {
    if (entry.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      return entry.kind;
    }
  }
  return null;
}

function splitIntoRawSections(
  content: string,
): { heading: string; body: string }[] {
  const headingPattern = /^#{2,3}\s+(.+)$/gm;
  const sections: { heading: string; body: string }[] = [];

  // Manual loop to find all matches (ES5 compatible)
  let match: RegExpExecArray | null;
  const matches: RegExpExecArray[] = [];

  // Reset regex and collect all matches
  headingPattern.lastIndex = 0;
  while ((match = headingPattern.exec(content)) !== null) {
    matches.push(match);
  }

  if (matches.length === 0) {
    return [];
  }

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const heading = match[1].trim();
    const start = (match.index ?? 0) + match[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : content.length;
    const body = content.slice(start, end).trim();
    // Keep every heading the model emitted, even one with an empty body —
    // dropping it silently would just make a card vanish from the dashboard
    // with no trace of why, which is worse than showing an empty-ish card.
    if (heading) {
      sections.push({ heading, body });
    }
  }
  return sections;
}

export function parseGCCV2Response(content: string): GCCV2ParsedResponse {
  const trimmed = (content || "").trim();
  if (!trimmed) {
    return { sections: [], raw: trimmed };
  }

  const rawSections = splitIntoRawSections(trimmed);

  if (rawSections.length === 0) {
    return {
      sections: [{ heading: "", body: trimmed, kind: "generic" }],
      raw: trimmed,
    };
  }

  const classified = rawSections.map((s) => ({
    ...s,
    kind: classifyHeading(s.heading),
  }));

  const allClassified = classified.every((s) => s.kind !== null);
  if (allClassified) {
    return {
      sections: classified.map((s) => ({
        heading: s.heading,
        body: s.body,
        kind: s.kind as GCCV2SectionKind,
      })),
      raw: trimmed,
    };
  }

  if (rawSections.length === POSITIONAL_ORDER.length) {
    return {
      sections: rawSections.map((s, idx) => ({
        heading: s.heading,
        body: s.body,
        kind: POSITIONAL_ORDER[idx],
      })),
      raw: trimmed,
    };
  }

  return {
    sections: rawSections.map((s) => ({
      heading: s.heading,
      body: s.body,
      kind: classifyHeading(s.heading) ?? "generic",
    })),
    raw: trimmed,
  };
}

// ─── Letter Never Sent — distinct 3-card shape ─────────────────────────────
export type GCCV2LetterCardKind =
  | "emotionalInsight"
  | "timingInsight"
  | "companionMessage";

export interface GCCV2LetterSection {
  heading: string;
  body: string;
  kind: GCCV2LetterCardKind;
}

export interface GCCV2LetterParsedResponse {
  sections: GCCV2LetterSection[];
  raw: string;
}

const LETTER_KEYWORD_MAP: { kind: GCCV2LetterCardKind; keywords: string[] }[] =
  [
    {
      kind: "emotionalInsight",
      keywords: [
        "emotional insight",
        "feeling",
        "emotion",
        "بصيرة عاطفية",
        "شعور",
      ],
    },
    {
      kind: "timingInsight",
      keywords: [
        "timing insight",
        "timing",
        "rhythm",
        "بصيرة التوقيت",
        "توقيت",
      ],
    },
    {
      kind: "companionMessage",
      keywords: ["companion message", "companion", "رسالة الرفيق", "رفيق"],
    },
  ];

const LETTER_POSITIONAL_ORDER: GCCV2LetterCardKind[] = [
  "emotionalInsight",
  "timingInsight",
  "companionMessage",
];

function classifyLetterHeading(heading: string): GCCV2LetterCardKind | null {
  const lower = heading.toLowerCase();
  for (const entry of LETTER_KEYWORD_MAP) {
    if (entry.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      return entry.kind;
    }
  }
  return null;
}

export function parseLetterReflection(
  content: string,
): GCCV2LetterParsedResponse {
  const trimmed = (content || "").trim();
  if (!trimmed) {
    return { sections: [], raw: trimmed };
  }

  const rawSections = splitIntoRawSections(trimmed);

  if (rawSections.length === 0) {
    return {
      sections: [
        {
          heading: "",
          body: trimmed,
          kind: "companionMessage",
        },
      ],
      raw: trimmed,
    };
  }

  if (rawSections.length === LETTER_POSITIONAL_ORDER.length) {
    const allClassified = rawSections.every(
      (s) => classifyLetterHeading(s.heading) !== null,
    );
    return {
      sections: rawSections.map((s, idx) => ({
        heading: s.heading,
        body: s.body,
        kind: allClassified
          ? (classifyLetterHeading(s.heading) as GCCV2LetterCardKind)
          : LETTER_POSITIONAL_ORDER[idx],
      })),
      raw: trimmed,
    };
  }

  return {
    sections: rawSections.map((s) => ({
      heading: s.heading,
      body: s.body,
      kind: classifyLetterHeading(s.heading) ?? "companionMessage",
    })),
    raw: trimmed,
  };
}
